/**
 * RecommendedGoals — surfaces curated Community Goals the member has not
 * joined yet, so the journeys stay discoverable from the dashboard.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorState } from "@/components/common/QueryState";
import { ArrowRight, Sparkles } from "lucide-react";

interface RecGoal {
  id: string;
  title: string;
  imageUrl: string | null;
  category: string | null;
}

export function RecommendedGoals() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recommended-community-goals", user?.id],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<RecGoal[]> => {
      let joinedIds: string[] = [];
      if (user?.id) {
        const { data: parts, error: partsError } = await supabase
          .from("common_goal_participants")
          .select("common_goal_id")
          .eq("user_id", user.id);
        if (partsError) throw partsError;
        joinedIds = (parts || []).map((p: any) => p.common_goal_id);
      }
      const { data: goals, error: goalsError } = await supabase
        .from("common_goals")
        .select("id, title, image_url, category, created_at")
        .order("created_at", { ascending: false })
        .limit(12);
      if (goalsError) throw goalsError;
      return (goals || [])
        .filter((g: any) => !joinedIds.includes(g.id))
        .slice(0, 3)
        .map((g: any) => ({
          id: g.id,
          title: g.title,
          imageUrl: g.image_url ?? null,
          category: g.category ?? null,
        }));
    },
  });

  if (isLoading) return <Skeleton className="h-32 w-full rounded-2xl" />;
  if (error) {
    return (
      <QueryErrorState
        error={error}
        onRetry={() => refetch()}
        title="We couldn't load recommendations"
      />
    );
  }
  if (!data || data.length === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2 pl-1">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="t-eyebrow">
          Recommended for you
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {data.map((g) => (
          <button
            key={g.id}
            type="button"
            style={{ touchAction: "manipulation" }}
            onClick={() => navigate(`/community-goals/${g.id}`)}
            className="group text-left rounded-2xl overflow-hidden border bg-card shadow-sm hover:shadow-md transition active:scale-[0.99]"
          >
            <div className="relative aspect-[16/9] bg-muted overflow-hidden">
              {g.imageUrl ? (
                <img
                  src={g.imageUrl}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20" />
              )}
            </div>
            <div className="p-3">
              <p className="text-sm font-semibold leading-tight line-clamp-2">{g.title}</p>
              <p className="text-xs text-primary font-semibold mt-1 inline-flex items-center gap-1">
                Preview journey <ArrowRight className="h-3 w-3" />
              </p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

export default RecommendedGoals;
